import { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';

import InviteUserModal from '../../components/InviteUserModal';
import DataTable from '../../components/table/DataTable';
import ActionMenu from '../../components/ui/ActionMenu';
import FormField from '../../components/ui/FormField';
import FormModal from '../../components/ui/FormModal';
import Modal from '../../components/ui/Modal';
import SearchBar from '../../components/ui/SearchBar';
import StatusMessage from '../../components/ui/StatusMessage';
import { useAuth } from '../../context/AuthContext';
import {
  USER_ROLES,
  getUsers,
} from '../../services/firestoreService';
import {
  deleteUserAccount as deleteUserAccountService,
  resetUserPasswordService,
  toggleUserStatusService,
  updateUserRoleService,
} from '../../services/userManagementService';

const roleLabels = {
  owner: 'Owner',
  admin: 'Admin',
  manager: 'Manager',
  staff: 'Staff',
};

const ROLE_HIERARCHY = {
  owner: 4,
  admin: 3,
  manager: 2,
  staff: 1,
};

const ADMIN_ROLES = new Set(['owner', 'admin']);

const actionsCellStyle = {
  width: 72,
  textAlign: 'right',
};

const statusMeta = {
  active: {
    label: 'Active',
    backgroundColor: '#ecfdf5',
    color: '#166534',
  },
  invited: {
    label: 'Invited',
    backgroundColor: '#eff6ff',
    color: '#1d4ed8',
  },
  inactive: {
    label: 'Inactive',
    backgroundColor: '#fef2f2',
    color: '#b91c1c',
  },
};

const roleClassNames = {
  owner: 'user-role-badge user-role-badge--owner',
  admin: 'user-role-badge user-role-badge--admin',
  manager: 'user-role-badge user-role-badge--manager',
  staff: 'user-role-badge user-role-badge--staff',
};

const getDisplayStatus = (user) =>
  user?.status === 'invited' ? 'invited' : user?.isActive ? 'active' : 'inactive';

const getStatusBadgeClassName = (status) =>
  `user-status-badge user-status-badge--${statusMeta[status] ? status : 'inactive'}`;

const normalizeCreatedAt = (value) => {
  if (!value) {
    return null;
  }

  if (typeof value?.toDate === 'function') {
    return value.toDate();
  }

  if (value instanceof Date) {
    return value;
  }

  const parsedDate = new Date(value);

  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const formatCreatedAt = (value) => {
  const date = normalizeCreatedAt(value);

  if (!date) {
    return '-';
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
};

const sortUsers = (users) =>
  [...users].sort((firstUser, secondUser) => {
    const firstDate = normalizeCreatedAt(firstUser.createdAt)?.getTime() || 0;
    const secondDate = normalizeCreatedAt(secondUser.createdAt)?.getTime() || 0;

    if (firstDate !== secondDate) {
      return secondDate - firstDate;
    }

    return (firstUser.name || firstUser.email || '').localeCompare(
      secondUser.name || secondUser.email || ''
    );
  });

const getRoleLevel = (role) => ROLE_HIERARCHY[role] || 0;

const hasPermission = (callerRole, targetRole) =>
  getRoleLevel(callerRole) > getRoleLevel(targetRole);

const getUserUid = (user) => user?.uid || user?.id || '';

export default function UsersList() {
  const { user: currentUser, role: currentRole, isLoading } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [statusUser, setStatusUser] = useState(null);
  const [editFormData, setEditFormData] = useState({ name: '', role: 'staff' });
  const [pageError, setPageError] = useState('');
  const [pageMessage, setPageMessage] = useState('');
  const [editError, setEditError] = useState('');
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [isSavingStatus, setIsSavingStatus] = useState(false);
  const [deleteUser, setDeleteUser] = useState(null);
  const [isDeletingUser, setIsDeletingUser] = useState(false);
  const [resettingPasswordUid, setResettingPasswordUid] = useState('');

  const currentUserId = currentUser?.uid || '';
  const canManageUsers = ADMIN_ROLES.has(currentRole);
  const canDeleteUsers = currentRole === 'owner';
  const assignableRoles = useMemo(
    () =>
      canManageUsers
        ? USER_ROLES.filter(
          (role) => role !== 'owner' && hasPermission(currentRole, role)
        )
        : [],
    [canManageUsers, currentRole]
  );

  const canManageTarget = (targetUser) =>
    canManageUsers &&
    targetUser?.role !== 'owner' &&
    hasPermission(currentRole, targetUser?.role);

  const canToggleTargetStatus = (targetUser) =>
    canManageTarget(targetUser) && getUserUid(targetUser) !== currentUserId;

  const canDeleteTarget = (targetUser) =>
    canDeleteUsers &&
    targetUser?.role !== 'owner' &&
    getUserUid(targetUser) !== currentUserId;

  const loadUsers = async ({ background = false } = {}) => {
    if (background) {
      setIsRefreshing(true);
    } else {
      setLoading(true);
    }

    setPageError('');

    try {
      const data = await getUsers();
      setUsers(sortUsers(Array.isArray(data) ? data : []));
    } catch (error) {
      console.error('Error fetching users:', error);
      setPageError('Failed to load users.');
    } finally {
      if (background) {
        setIsRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  };

  const refetchUsers = () => loadUsers({ background: true });

  useEffect(() => {
    loadUsers();
  }, []);

  const filteredUsers = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();

    if (!query) {
      return users;
    }

    return users.filter((user) =>
      [user.name, user.email, user.role]
        .some((value) => String(value || '').toLowerCase().includes(query))
    );
  }, [searchTerm, users]);

  const handleOpenEditModal = (user) => {
    if (!canManageTarget(user)) {
      return;
    }

    setSelectedUser(user);
    setEditFormData({
      name: user.name || '',
      role: assignableRoles.includes(user.role)
        ? user.role
        : assignableRoles[0] || 'staff',
    });
    setEditError('');
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    if (isSavingEdit) {
      return;
    }

    setIsEditModalOpen(false);
    setSelectedUser(null);
    setEditFormData({ name: '', role: 'staff' });
    setEditError('');
  };

  const handleEditUser = async (event) => {
    event.preventDefault();

    const selectedUserUid = getUserUid(selectedUser);

    if (!selectedUserUid) {
      return;
    }

    const name = editFormData.name.trim();
    const role = editFormData.role;

    if (!name) {
      setEditError('Name is required.');
      return;
    }

    if (!assignableRoles.includes(role)) {
      setEditError('You can only assign roles below your own role.');
      return;
    }

    setEditError('');
    setIsSavingEdit(true);

    try {
      const updatedUser = await updateUserRoleService(selectedUserUid, role, name);

      setUsers((currentUsers) =>
        sortUsers(
          currentUsers.map((user) =>
            getUserUid(user) === selectedUserUid ? { ...user, ...updatedUser } : user
          )
        )
      );
      setPageMessage('User updated.');
      setPageError('');
      setIsEditModalOpen(false);
      setSelectedUser(null);
      setEditFormData({ name: '', role: 'staff' });
      setEditError('');
    } catch (error) {
      console.error('UPDATE USER ERROR:', error);
      setEditError(error.message || 'Failed to update user.');
      await loadUsers({ background: true });
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleToggleStatus = async () => {
    const statusUserUid = getUserUid(statusUser);

    if (!statusUserUid || isSavingStatus) {
      return;
    }

    const nextStatus = !statusUser.isActive;

    if (statusUserUid === currentUserId && nextStatus === false) {
      setPageError('You cannot deactivate your own account.');
      setStatusUser(null);
      return;
    }

    if (statusUser?.role === 'owner' && nextStatus === false) {
      setPageError('Owner users cannot be deactivated.');
      setStatusUser(null);
      return;
    }

    console.log('UsersList toggleUserStatus confirm:', {
      uid: statusUserUid,
      isActive: nextStatus,
    });

    setIsSavingStatus(true);
    setPageError('');
    setPageMessage('');

    try {
      const updatedUser = await toggleUserStatusService(statusUserUid, nextStatus);

      console.log('UsersList toggleUserStatus success:', {
        uid: statusUserUid,
        isActive: updatedUser?.isActive,
      });

      if (updatedUser) {
        setUsers((currentUsers) =>
          sortUsers(
            currentUsers.map((user) =>
              getUserUid(user) === statusUserUid ? { ...user, ...updatedUser } : user
            )
          )
        );
      }

      setPageMessage(`User ${nextStatus ? 'activated' : 'deactivated'} successfully.`);
      setPageError('');
      setStatusUser(null);
      await loadUsers({ background: true });
    } catch (error) {
      console.error('UPDATE USER STATUS ERROR:', error);
      setPageError(error.message || 'Failed to update user status.');
      await loadUsers({ background: true });
    } finally {
      setIsSavingStatus(false);
    }
  };

  const handleDeleteUser = async () => {
    const deleteUserUid = getUserUid(deleteUser);

    if (!deleteUserUid || isDeletingUser) {
      return;
    }

    setIsDeletingUser(true);
    setPageError('');
    setPageMessage('');

    try {
      await deleteUserAccountService(deleteUserUid);
      setUsers((currentUsers) =>
        currentUsers.filter((user) => getUserUid(user) !== deleteUserUid)
      );
      setPageMessage('User deleted.');
      setDeleteUser(null);
    } catch (error) {
      console.error('DELETE USER ERROR:', error);
      setPageError(error.message || 'Failed to delete user.');
      await loadUsers({ background: true });
    } finally {
      setIsDeletingUser(false);
    }
  };

  const handleResetPassword = async (user) => {
    const targetUid = getUserUid(user);
    const targetEmail = String(user?.email || '').trim().toLowerCase();

    if (!targetUid || resettingPasswordUid) {
      return;
    }

    if (!targetEmail) {
      setPageError('User email is missing.');
      return;
    }

    if (!canManageTarget(user)) {
      setPageError('You can only manage users below your own role.');
      return;
    }

    setResettingPasswordUid(targetUid);
    setPageError('');
    setPageMessage('');

    try {
      await resetUserPasswordService(targetEmail);
      setPageMessage('Password reset email sent');
    } catch (error) {
      console.error('RESET USER PASSWORD ERROR:', error);
      setPageError(error.message || 'Failed to reset user password.');
    } finally {
      setResettingPasswordUid('');
    }
  };

  if (!isLoading && !currentUser) {
    return <Navigate to="/" replace />;
  }

  const columns = [
    { key: 'name', label: 'Name' },
    { key: 'email', label: 'Email' },
    { key: 'role', label: 'Role' },
    { key: 'status', label: 'Status' },
    { key: 'createdAt', label: 'Created At' },
    ...(canManageUsers ? [{ key: 'actions', label: '' }] : []),
  ];

  const statusModalActionLabel = statusUser?.isActive ? 'Deactivate' : 'Activate';

  return (
    <div className="page page--compact-list users-page">
      <div className="page-header">
        <div className="page-title-block">
          <h1 className="page-title">Users</h1>
          <p className="page-subtitle">
            Manage user accounts, roles, and active access.
          </p>
        </div>

        {canManageUsers ? (
          <div className="page-header__actions">
            <button
              type="button"
              className="button button-primary"
              onClick={() => setInviteOpen(true)}
              disabled={loading || isRefreshing}
            >
              + Invite User
            </button>
          </div>
        ) : null}
      </div>

      <StatusMessage tone="success" message={pageMessage} />
      <StatusMessage tone="error" message={pageError} />

      <section className="section users-list-section">
        <div className="controls-bar users-controls-bar">
          <div className="controls-bar__search">
            <SearchBar
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search users by name, email, or role..."
            />
          </div>
          <span className="users-count">
            {filteredUsers.length} {filteredUsers.length === 1 ? 'user' : 'users'}
          </span>
        </div>

        {loading ? <p className="page-subtitle">Loading users...</p> : null}
        {!loading && isRefreshing ? (
          <p className="page-subtitle">Refreshing users...</p>
        ) : null}

        {!loading ? (
          <div className="table-wrapper list-table-wrapper">
            <DataTable
              columns={columns}
              data={filteredUsers}
              emptyMessage="No users found."
              renderRow={(user) => (
                <tr key={user.id} className="users-table-row" style={{ cursor: 'default' }}>
                  <td>
                    <div className="user-identity">
                      <span className="user-avatar" aria-hidden="true">
                        {(user.name || user.email || '?').trim().charAt(0).toUpperCase()}
                      </span>
                      <span className="user-name-text">
                        {user.name || '-'}
                      </span>
                    </div>
                  </td>
                  <td>{user.email || '-'}</td>
                  <td>
                    <span className={roleClassNames[user.role] || roleClassNames.staff}>
                      {roleLabels[user.role] || user.role || '-'}
                    </span>
                  </td>
                  <td>
                    <span className={getStatusBadgeClassName(getDisplayStatus(user))}>
                      {statusMeta[getDisplayStatus(user)]?.label || 'Inactive'}
                    </span>
                  </td>
                  <td>{formatCreatedAt(user.createdAt)}</td>
                  {canManageUsers ? (
                    <td
                      style={actionsCellStyle}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <ActionMenu
                        items={[
                          canManageTarget(user)
                            ? {
                              key: 'edit',
                              label: 'Edit User',
                              onSelect: () => handleOpenEditModal(user),
                              disabled:
                                isSavingEdit ||
                                isSavingStatus ||
                                isDeletingUser ||
                                Boolean(resettingPasswordUid),
                            }
                            : null,
                          canManageTarget(user)
                            ? {
                              key: 'reset-password',
                              label:
                                resettingPasswordUid === getUserUid(user)
                                  ? 'Sending Reset...'
                                  : 'Reset Password',
                              onSelect: () => handleResetPassword(user),
                              disabled:
                                isSavingEdit ||
                                isSavingStatus ||
                                isDeletingUser ||
                                Boolean(resettingPasswordUid),
                            }
                            : null,
                          canToggleTargetStatus(user)
                            ? {
                              key: 'status',
                              label: user.isActive ? 'Deactivate' : 'Activate',
                              onSelect: () => {
                                const targetUid = getUserUid(user);

                                console.log('UsersList toggleUserStatus selected:', {
                                  uid: targetUid,
                                  isActive: !user.isActive,
                                });

                                if (!targetUid) {
                                  setPageError('User uid is missing.');
                                  return;
                                }

                                setStatusUser({ ...user, uid: targetUid });
                              },
                              tone: user.isActive ? 'danger' : 'default',
                              disabled:
                                isSavingEdit ||
                                isSavingStatus ||
                                isDeletingUser ||
                                Boolean(resettingPasswordUid),
                            }
                            : null,
                          canDeleteTarget(user)
                            ? {
                              key: 'delete',
                              label: 'Delete User',
                              onSelect: () => setDeleteUser(user),
                              tone: 'danger',
                              disabled:
                                isSavingEdit ||
                                isSavingStatus ||
                                isDeletingUser ||
                                Boolean(resettingPasswordUid),
                            }
                            : null,
                        ].filter(Boolean)}
                      />
                    </td>
                  ) : null}
                </tr>
              )}
            />
          </div>
        ) : null}
      </section>

      <InviteUserModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onSuccess={refetchUsers}
      />

      <FormModal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        onSubmit={handleEditUser}
        title="Edit User"
        submitLabel={isSavingEdit ? 'Saving...' : 'Save User'}
        submitDisabled={isSavingEdit}
        cancelDisabled={isSavingEdit}
        errorMessage={editError}
      >
        <FormField
          name="edit-name"
          label="Name"
          required
          value={editFormData.name}
          onChange={(value) => {
            setEditFormData((prev) => ({ ...prev, name: value }));
            setEditError('');
          }}
        />
        <div className="form-group">
          <label htmlFor="field-edit-role">Role *</label>
          <select
            id="field-edit-role"
            className="select"
            value={editFormData.role}
            required
            onChange={(event) => {
              setEditFormData((prev) => ({ ...prev, role: event.target.value }));
              setEditError('');
            }}
          >
            {assignableRoles.map((role) => (
              <option key={role} value={role}>
                {roleLabels[role]}
              </option>
            ))}
          </select>
        </div>
      </FormModal>

      <Modal
        isOpen={Boolean(statusUser)}
        onClose={() => {
          if (!isSavingStatus) {
            setStatusUser(null);
          }
        }}
        onConfirm={handleToggleStatus}
        title={`${statusModalActionLabel} User`}
        description={`Are you sure you want to ${statusModalActionLabel.toLowerCase()} ${
          statusUser?.name || statusUser?.email || 'this user'
        }?`}
        confirmLabel={
          isSavingStatus ? `${statusModalActionLabel}...` : statusModalActionLabel
        }
        confirmDisabled={isSavingStatus}
        cancelDisabled={isSavingStatus}
        confirmButtonStyle={
          statusUser?.isActive
            ? undefined
            : {
              backgroundColor: '#166534',
              borderColor: '#166534',
              color: '#ffffff',
            }
        }
      />

      <Modal
        isOpen={Boolean(deleteUser)}
        onClose={() => {
          if (!isDeletingUser) {
            setDeleteUser(null);
          }
        }}
        onConfirm={handleDeleteUser}
        title="Delete User"
        description={`Are you sure you want to permanently delete ${
          deleteUser?.name || deleteUser?.email || 'this user'
        }?`}
        confirmLabel={isDeletingUser ? 'Deleting...' : 'Delete'}
        confirmDisabled={isDeletingUser}
        cancelDisabled={isDeletingUser}
      />
    </div>
  );
}
